import { Namespace, TFunction } from 'react-i18next';
import { prepareLeadChannelGroups } from 'pages/inbox/filter/ThreadFilterForm';
import { orderBy } from 'utils/arrayUtils';
import { typedValues } from 'utils/funcs';
import { Property } from 'models/Properties';
import {
  PipelineFilter,
  PipelineFilterFormValues,
  PipelineFilterLeadFormValues,
  PropertyTagOption,
} from '../Pipeline.types';
import {
  HostfullyLeadChannel,
  Lead,
  LeadChannel,
  LeadChannelTO,
  LeadGroupedStatus,
  LeadStatus,
} from '../../../models/Leads';

const statusesMapping = {
  [LeadStatus.ON_HOLD]: [LeadStatus.ON_HOLD, LeadStatus.HOLD_EXPIRED],
};

const mapPipelineFilterFormValues = {
  lead: (leads: Lead[]) => ({
    uids: leads?.length ? [leads[0].uid] : '',
  }),
  leadStatus: (leadStatus: LeadStatus | string) => {
    if (!leadStatus) {
      return { groupedStatus: null, statuses: null };
    }

    if (Object.keys(LeadGroupedStatus).includes(leadStatus)) {
      return { groupedStatus: leadStatus, statuses: null };
    }

    return {
      groupedStatus: null,
      // @ts-expect-error TS7053 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      statuses: statusesMapping[leadStatus] || [leadStatus],
    };
  },
  propertyUid: (propertyUid: string) => ({
    propertyUids: propertyUid ? [propertyUid] : [],
  }),
  channel: (channel: LeadChannel) => ({
    channels: channel ? [channel] : [],
  }),
  userUid: (userUidValue: string) => ({
    userUids: userUidValue ? [userUidValue] : [],
  }),
  range: (
    rangeValue:
      | {
          checkInFrom: Date | null;
          checkInTo: Date | null;
        }
      | undefined,
  ) => ({
    checkInFrom: rangeValue?.checkInFrom,
    checkInTo: rangeValue?.checkInTo,
  }),
};

export function getPropertyUidsMatchingTags(
  properties: Property[],
  propertyTags: PropertyTagOption[],
): string[] {
  if (!propertyTags?.length) {
    return [];
  }

  const selectedTags = Array.from(
    new Set(propertyTags.map(({ value }) => value.toLowerCase())),
  );

  const hasAllSelectedTags = (tags?: string[]) => {
    if (!tags?.length) {
      return false;
    }
    const loweredTags = new Set(tags.map((tag) => tag.toLowerCase()));
    return selectedTags.every((tag) => loweredTags.has(tag));
  };

  const uids = new Set<string>();
  properties.forEach((property) => {
    if (hasAllSelectedTags(property.tags)) {
      uids.add(property.uid);
    }
    property.unitTypes?.forEach((unitType) => {
      if (hasAllSelectedTags(unitType.tags)) {
        uids.add(unitType.uid);
      }
    });
    property.subUnits?.forEach((subUnit) => {
      if (hasAllSelectedTags(subUnit.tags)) {
        uids.add(subUnit.uid);
      }
    });
  });

  return Array.from(uids);
}

export function convertPipelineFilterFormValues(
  pipelineFilterFormValues:
    | PipelineFilterFormValues
    | PipelineFilterLeadFormValues,
): PipelineFilter {
  // @ts-expect-error TS2740 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  return Object.entries(pipelineFilterFormValues).reduce((values, entry) => {
    const key = entry[0];
    const value = entry[1];
    // @ts-expect-error TS7053 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    const fieldMapper = mapPipelineFilterFormValues[key];
    return {
      ...values,
      ...(fieldMapper ? fieldMapper(value) : { [key]: value }),
    };
  }, {});
}

type PipelineChannelOption =
  | { label: string; value: LeadChannel }
  | { label: string; value: HostfullyLeadChannel };

const HOSTFULLY_LEAD_CHANNELS_ORDER = [
  HostfullyLeadChannel.HOSTFULLY_PMP,
  HostfullyLeadChannel.HOSTFULLY_DIRECT_BOOKING,
  HostfullyLeadChannel.HOSTFULLY_API,
];

export function preparePipelineLeadChannelGroups(
  leadChannels: LeadChannelTO[],
  t: TFunction<Namespace<'en'>>,
) {
  const optionGroups = prepareLeadChannelGroups(leadChannels, t);

  return optionGroups.map((group) => {
    return {
      ...group,
      options: group.options.flatMap((option): PipelineChannelOption[] => {
        if (option.value === LeadChannel.HOSTFULLY) {
          const orderedChannels = orderBy(typedValues(HostfullyLeadChannel), [
            {
              field: (c) => HOSTFULLY_LEAD_CHANNELS_ORDER.indexOf(c),
              order: 'asc',
            },
          ]);

          return orderedChannels.map((channel) => ({
            label: t('form.leadHostfullyChannel', { context: channel }),
            value: channel as HostfullyLeadChannel,
          }));
        }

        return [option];
      }),
    };
  });
}
